import { type ErrorResponse, onError } from "@apollo/client/link/error";
import { broadcastLogout } from "common/utils/coordinatedLogout";

/**
 * Global error interceptor
 */
export enum ErrorOptionsEnum {
  LoggedOut,
}
type HandleErrorFunction = (errorLinkParams: ErrorResponse) => void;
type ErrorHandlerMap = Partial<Record<ErrorOptionsEnum, HandleErrorFunction>>;
export const overrideErrorHandlers: ErrorHandlerMap = {};

export function overrideGQLErrorHandler(
  errorType: ErrorOptionsEnum,
  callback: HandleErrorFunction,
) {
  if (errorType in overrideErrorHandlers) {
    // There can only be one override per handler - something bad has happened
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line
      console.error(`Cannot override error handler ${errorType}, it is already set.`);
    }
  } else {
    overrideErrorHandlers[errorType] = callback;
  }
}

const errorLink = onError((errorLinkParams) => {
  const { graphQLErrors, networkError } = errorLinkParams;
  const isLoggedOut =
    graphQLErrors &&
    graphQLErrors.some(({ extensions }) => {
      return (
        extensions &&
        typeof extensions.error_code === "string" &&
        ["STAFF_UNAUTHENTICATED_ACCESS", "GLOBAL_PORTAL_UNAUTHENTICATED_ACCESS"].includes(
          extensions?.error_code,
        )
      );
    });

  if (process.env.NODE_ENV !== "production" && !isLoggedOut) {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ extensions, message, locations, path }) =>
        // eslint-disable-next-line
        console.warn(
          `[GraphQL error]: Code: ${extensions?.error_code}, Message: ${message}, Location: ` +
            `${locations}, Path: ${path}`,
        ),
      );
    }

    if (networkError) {
      // eslint-disable-next-line
      console.warn(`[Network error]: ${networkError}`);
    }
  }

  // if the user's been logged out and not on the login page, redirect them to the login page
  if (isLoggedOut && !location.pathname.includes("/login")) {
    broadcastLogout();
    const overrideCallback = overrideErrorHandlers[ErrorOptionsEnum.LoggedOut];
    if (overrideCallback) {
      overrideCallback(errorLinkParams);
    } else {
      location.assign(`/login?path=${encodeURIComponent(location.pathname)}`);
    }
  }
});

export default errorLink;
